import type { PetOwner, QuoteReponse } from "@/shared/http/lead-api"
import { ROUTES } from "@/shared/routing/routes"

/**
 * The owner fields the recap page displays and the payment needs; it cannot collect them itself.
 * Same rules as the pages that collect them (ownerNameSchema, ownerAddressSchema): names of at
 * least two characters, a five-character zipcode, a country.
 */
function hasIdentityAndAddress(owner: PetOwner | undefined): boolean {
  return Boolean(
    owner &&
      owner.owner_firstname?.trim().length >= 2 &&
      owner.owner_lastname?.trim().length >= 2 &&
      owner.address?.street_nb &&
      owner.address.street_name &&
      owner.address.zipcode?.length === 5 &&
      owner.address.city &&
      owner.address.country
  )
}

/** Same rule as ownerBirthdateSchema: a non-blank birth city next to the birthday. */
function hasBirthInformations(owner: PetOwner | undefined): boolean {
  return Boolean(owner?.owner_birthday && owner.owner_birthcity?.trim())
}

export function computeRedirectRoute(quoteResponse: QuoteReponse): string {
  const pets = quoteResponse.data
  const owner = quoteResponse.pet_owner
  const hasAnyFormula = pets.some((p) => p.options || p.coverage)
  const allPetsHaveFormula = pets.every((p) => p.options || p.coverage)

  // CONVERSION: at least one pet has a formula
  if (hasAnyFormula) {
    if (!allPetsHaveFormula) return ROUTES.prepackages.path
    if (owner?.owner_birthday) {
      // In the web funnel the birthday is the last owner step, so a birthday means a complete
      // owner. A quote filled through the API (MCP app) can carry a birthday with holes: send
      // the user to the first owner page still missing something, prefilled, instead of a recap
      // that cannot collect it [DLM-2120].
      if (!hasIdentityAndAddress(owner)) return ROUTES.userInformations.path
      if (!hasBirthInformations(owner)) return ROUTES.userBirthInformations.path
      return ROUTES.recap.path
    }
    return ROUTES.tribeQuote.path
  }

  // LEAD GENERATION: no formula yet
  // A real email means the user passed /attributionQuestion + /userMailPhone
  // and reached /prepackages — return them there regardless of pet count.
  // "_" is the backend placeholder when a landing page created the quote
  // without asking for an email, so treat it as "no email entered".
  if (owner?.owner_email && owner.owner_email !== "_") return ROUTES.prepackages.path
  return ROUTES.tribe.path
}
